import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import FlashcardCard from "../../../components/flashcard/FlashcardCard";
import { reviewDeskApi } from "../../../api/reviewDeskApi";
import "./ReviewFlashCardScore.css";

const QUEUE_LIMIT = 25;
const RATING_OPTIONS = [
  { key: "1", value: 0, label: "Again", desc: "Quên hoàn toàn" },
  { key: "2", value: 1, label: "Hard", desc: "Nhớ khó" },
  { key: "3", value: 2, label: "Good", desc: "Nhớ bình thường" },
  { key: "4", value: 3, label: "Easy", desc: "Nhớ rất tốt" },
];
const STATE_LABEL = {
  0: "New",
  1: "Learning",
  2: "Review",
  3: "Relearning",
  9: "Suspended",
};

const parseDate = (input) => {
  if (!input) return null;
  const date = new Date(input);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateTime = (input) => {
  const date = parseDate(input);
  if (!date) return "--";
  return date.toLocaleString();
};

const normalizeQueueItem = (item) => {
  const cardId = item?.cardId ?? item?.CardId;
  const state = Number(item?.state ?? item?.State ?? 0);
  return {
    id: cardId,
    deckId: item?.deckId ?? item?.DeckId,
    frontText: item?.frontText ?? item?.FrontText ?? "",
    backText: item?.backText ?? item?.BackText ?? "",
    voiceUrl: item?.voiceUrl ?? item?.VoiceUrl ?? item?.audioUrl ?? item?.AudioUrl ?? null,
    state,
    stateLabel: STATE_LABEL[state] ?? `State ${state}`,
    dueAt: item?.dueAt ?? item?.DueAt ?? null,
    stepIndex: item?.stepIndex ?? item?.StepIndex ?? null,
    intervalDays: Number(item?.intervalDays ?? item?.IntervalDays ?? 0),
    ease: Number(item?.ease ?? item?.Ease ?? 0),
  };
};

export default function ReviewFlashCardScore() {
  const location = useLocation();
  const { state } = location;
  const navigate = useNavigate();
  const deskId = state?.id ?? new URLSearchParams(location.search).get("deskId");
  const [queue, setQueue] = useState([]);
  const [ratedCount, setRatedCount] = useState(0);
  const [ratingStats, setRatingStats] = useState({ 0: 0, 1: 0, 2: 0, 3: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isReschedule, setIsReschedule] = useState(true);
  const [error, setError] = useState("");
  const cardStartRef = useRef(Date.now());
  const totalSeen = ratedCount + queue.length;
  const progressPercent = totalSeen ? Math.round((ratedCount / totalSeen) * 100) : 0;
  const currentCard = queue[0] ?? null;

  const extractQueueItems = (response) =>
    response?.items ??
    response?.Items ??
    response?.data?.items ??
    response?.data?.Items ??
    [];

  const loadQueue = useCallback(async ({ silent = false } = {}) => {
    if (!deskId) {
      setError("Không tìm thấy category/desk để review.");
      return;
    }
    if (!silent) {
      setIsLoading(true);
    }
    setError("");
    try {
      const response = await reviewDeskApi.getStudyQueue(deskId, {
        limit: QUEUE_LIMIT,
        includeNew: true,
      });
      const rawItems = extractQueueItems(response);
      setQueue(rawItems.map(normalizeQueueItem).filter((item) => !!item.id));
    } catch (err) {
      setError(err?.message || "Không tải được queue review. Vui lòng thử lại.");
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  }, [deskId]);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!currentCard || isSubmitting) return;
      const option = RATING_OPTIONS.find((item) => item.key === e.key);
      if (option) {
        e.preventDefault();
        handleRate(option.value);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCard?.id, isSubmitting]);

  useEffect(() => {
    cardStartRef.current = Date.now();
  }, [currentCard?.id]);

  const handleRate = async (rating) => {
    if (!currentCard) return;
    setIsSubmitting(true);
    setError("");
    try {
      const answerMs = Math.max(0, Date.now() - cardStartRef.current);
      await reviewDeskApi.reviewCard({
        cardId: currentCard.id,
        rating,
        reschedule: isReschedule,
        answerMs,
      });

      setRatingStats((prev) => ({
        ...prev,
        [rating]: (prev[rating] || 0) + 1,
      }));
      setRatedCount((prev) => prev + 1);
      await loadQueue({ silent: true });
    } catch (err) {
      setError(err?.message || "Không gửi được kết quả review.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container py-4 review-session">
      <section className="review-header mb-4">
        <div>
          <h3 className="review-title mb-1">Flashcard Review Session</h3>
          <p className="review-subtitle mb-0">
            {state?.name || "Category"} • Rated: {ratedCount} • In queue: {queue.length}
          </p>
        </div>
        <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate("/review/category")}>
          Back
        </button>
      </section>

      <div className="review-progress mb-4">
        <div className="d-flex justify-content-between small mb-2">
          <span>Done: {ratedCount}/{totalSeen}</span>
          <span>{progressPercent}%</span>
        </div>
        <div className="progress" role="progressbar" aria-valuenow={progressPercent}>
          <div className="progress-bar" style={{ width: `${progressPercent}%` }}></div>
        </div>
      </div>

      {isLoading && <div className="text-muted">Loading review queue...</div>}
      {error && <div className="alert alert-danger py-2">{error}</div>}

      {!isLoading && !error && queue.length === 0 && (
        <div className="alert alert-info">Không còn thẻ đến hạn để review.</div>
      )}

      {!isLoading && !error && queue.length > 0 && (
        <div className="row g-4">
          <div className="col-lg-8">
            <div className="review-main-card">
              <div className="d-flex justify-content-center mt-2 mb-4">
                <FlashcardCard
                  card={currentCard}
                  detailsState={{
                    returnPath: "/review/flashcard",
                    returnState: {
                      id: deskId,
                      name: state?.name,
                    },
                  }}
                />
              </div>

              <div className="anki-rating-grid">
                {RATING_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`btn anki-rate-btn rating-${option.label.toLowerCase()}`}
                    disabled={isSubmitting}
                    onClick={() => handleRate(option.value)}
                    title={`${option.key}. ${option.label}`}
                  >
                    <span className="anki-key">{option.key}</span>
                    <span className="anki-label">{option.label}</span>
                    <small className="anki-desc">{option.desc}</small>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="review-side-card">
              <h6 className="fw-bold mb-3">Session Stats</h6>
              <div className="stat-grid">
                {RATING_OPTIONS.map((item) => (
                  <div key={item.value} className="stat-item">
                    <div className="stat-value">{ratingStats[item.value] || 0}</div>
                    <div className="stat-label">{item.label}</div>
                  </div>
                ))}
              </div>

              <hr />

              <h6 className="fw-bold mb-2">Current Card</h6>
              <div className="small text-muted mb-2">
                <div>State: <b>{currentCard?.stateLabel || "--"}</b></div>
                <div>Due: <b>{formatDateTime(currentCard?.dueAt)}</b></div>
                <div>Interval: <b>{currentCard?.intervalDays ?? 0} day(s)</b></div>
                <div>Ease: <b>{(currentCard?.ease ?? 0).toFixed(2)}</b></div>
              </div>

              <div className="form-check form-switch mb-3">
                <input
                  id="review-reschedule"
                  className="form-check-input"
                  type="checkbox"
                  checked={isReschedule}
                  onChange={(e) => setIsReschedule(e.target.checked)}
                  disabled={isSubmitting}
                />
                <label className="form-check-label small" htmlFor="review-reschedule">
                  Reschedule theo thuật toán Anki
                </label>
              </div>

              <h6 className="fw-bold mb-2">Keyboard Shortcuts</h6>
              <div className="shortcut-list">
                <div>
                  <span className="kbd">1..4</span> Again / Hard / Good / Easy
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
